import { Injectable } from '@angular/core';
import { OperationModeService } from './operation-mode.service';
import { ActivatedRoute, Router } from '@angular/router';
import { LocalStorageService } from './local-storage.service';
import { DEFAULT_MODAL_KAMONA_CLASSES, DEFAULT_MODAL_WIDTH, fvsConstants, MONIEPOINT_DESIGN_TYPE, UserAlias } from '../../shared/constants';
import { AuthenticationService } from './authentication.service';
import { StringHelper } from '../helpers/strings.helper';
import { ComplianceService } from './compliance.service';
import { AppConfig } from '../../shared/config/app-config';
import { KycService } from './kyc-service';
import { DeviceInformation } from '../../shared/models/device.model';
import { DeviceService } from './device.service';
import { MobileWebViewHelperService } from './mobile-web-view-helper.service';
import { MobileWebViewService } from './mobile-web-view.service';
import { KYCFormRoute } from '../models/kyc.model';
import { CookieService } from 'ngx-cookie-service';
import { EncryptionUtil } from '../../shared/utils/encryption.util';
import { ModalOptionsConfig, ModalService } from '../../lean-shared/components/nz-modal-service/modal.service';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { DataLayerService } from './data-layer-service.service';
import { initializeDatalayer } from '../../shared/models/data.layer.model';
import { KycRoutes, RolesEligibleForSoftOnboarding, TOKEN_DOMAINS } from '../models/onboarding/onboarding-v2-model';
import { GeneralService } from './general.service';
import { StorageKeys } from '../../shared/models/storage-keys';
import { OnboardingV2Service } from './onboarding-v2.service';
import { StaffManagementService } from './staff-management/staff-management.service';
import { ClusterManagerManagementService } from './cluster-manager-management/cluster-manager-management.service';
import { DeviceHelperService } from './device-helper.service';
import { FVSHelperService } from './fvs/fvs-helper.service';
import { FVSBRMLGAUpdateHelperService } from './fvs/fvs-brm-lga-update-helper.service';

declare const loginZendesk: any;

@Injectable({
    providedIn: 'root',
})
export class LoginHelperService {
    businesses: any[] = [];
    registerFireBaseToken: boolean;
    isMobileWebviewUser: boolean;
    loginOtpResponse: any;
    staticAgent: boolean;
    runningModal: NzModalRef;
    kioskMarketerOnboardingStageRequest: Promise<{responseBody: {status: string}}>;
    private userRole;
    private deviceInformation: DeviceInformation;

    constructor(
        private operationService: OperationModeService,
        private router: Router,
        private localStorage: LocalStorageService,
        public complianceService: ComplianceService,
        public config: AppConfig,
        private auth: AuthenticationService,
        private activatedRoute: ActivatedRoute,
        private deviceService: DeviceService,
        private kycService: KycService,
        private mobileWebViewHelperService: MobileWebViewHelperService,
        private mobileService: MobileWebViewService,
        private cookie: CookieService,
        private encryptionUtil: EncryptionUtil,
        private modalService: ModalService,
        private dataLayerService: DataLayerService,
        private generalService: GeneralService,
        private onboardingService: OnboardingV2Service,
        public staffManagementService: StaffManagementService,
        public clusterManagerManagementService: ClusterManagerManagementService,
        public deviceHelperService: DeviceHelperService,
        private fvsHelperService: FVSHelperService,
        private fvsBRMLGAUpdateHelperService: FVSBRMLGAUpdateHelperService,
    ) {}

    async processSessionUser(res, deviceInformation: DeviceInformation, registerFireBaseToken = null, skipNavigation = false) {
        if (this.isNewStaffLogin() && res?.tokenData?.refresh_token) {
            this.saveStaffRefreshTokenToStorage(res?.tokenData?.refresh_token, res?.tokenData?.refreshTokenExpireIn);
        }
        if (this.clusterManagerManagementService.isNewClusterManagerLogin() && res?.tokenData?.refresh_token) {
            this.clusterManagerManagementService.saveClusterManagerRefreshTokenToStorage(res?.tokenData?.refresh_token);
        }
        this.saveTokenData(res?.tokenData);
        registerFireBaseToken = registerFireBaseToken ?? res?.notificationToken;
        await this.setSessionCredentials(res, registerFireBaseToken, deviceInformation);
        this.kioskMarketerOnboardingStageRequest = this.getKMOnboardingStatus(res?.tokenData);
        await this.getUserTypeInfo();

        this.dataLayerService.identifyUser();

        if (!skipNavigation) {
            this.navigateSignInUser(null, false, res?.tokenData);
        }
    }

    async saveStaffUserTokenData(res, shouldRoute = false) {
        await this.deleteStaffUserRefreshTokenFlowData();
        if (
            this.isStaffUserRefreshTokenEnabled() &&
            res?.tokenData &&
            res?.tokenData?.refresh_token &&
            res?.tokenData?.roles.includes(UserAlias.staffUser)
        ) {
            const userData = { email: res?.tokenData?.email, name: this.getStaffUserFullName(res?.tokenData) };
            this.localStorage.saveAll(userData);
            this.localStorage.save(StorageKeys.roles, JSON.stringify(res?.tokenData?.roles));
            let refreshTokenData = res;
            this.setStaffBusinesses(refreshTokenData.businesses);
            this.staffManagementService.tokenDataDeviceResponse = refreshTokenData;
            this.saveStaffRefreshTokenToStorage(refreshTokenData?.tokenData?.refresh_token, refreshTokenData?.tokenData?.refreshTokenExpiresIn);
            if (shouldRoute) {
                this.routeToBusinessSelection();
            }
            return;
        }
    }

    getStaffUserFullName(data) {
        const fistName = data.firstName || '';
        const lastName = data.lastName || '';
        return `${fistName} ${lastName}`;
    }

    async saveClusterManagerUserTokenData(res, shouldRoute = false) {
        await this.clusterManagerManagementService.deleteClusterManagerUserRefreshTokenFlowData();
        if (
            res?.tokenData?.refresh_token &&
            res?.tokenData?.roles.includes(UserAlias.clusterManager)
        ) {
            const userData = { email: res?.tokenData?.email, name: this.getClusterManagerUserFullName(res?.tokenData) };
            this.localStorage.saveAll(userData);
            this.localStorage.save(StorageKeys.roles, JSON.stringify(res?.tokenData?.roles));
            let refreshTokenData = res;
            this.clusterManagerManagementService.tokenDataDeviceResponse = refreshTokenData;
            this.clusterManagerManagementService.saveClusterManagerRefreshTokenToStorage(res?.tokenData?.refresh_token);
            if (shouldRoute) {
                this.navigateClusterManager();
            }
            return;
        }
    }

    getClusterManagerUserFullName(data) {
        const fistName = data.firstName || '';
        const lastName = data.lastName || '';
        return `${fistName} ${lastName}`;
    }

    async saveDataOnChangeStaffUserBusiness(res) {
        const deviceInformation = this.deviceHelperService.getDeviceInformation();
        await this.processSessionUser(res, deviceInformation, null, true);
    }

    async saveDataOnChangeClusterManagerRole(res) {
        const deviceInformation = this.deviceHelperService.getDeviceInformation();
        await this.processSessionUser(res, deviceInformation);
    }

    saveStaffRefreshTokenToStorage(refreshToken, refreshTokenExpireIn) {
        this.localStorage.save(StorageKeys.STAFF_USER_REFRESH_TOKEN, JSON.stringify(refreshToken));
        this.localStorage.save(StorageKeys.STAFF_USER_REFRESH_TOKEN_EXPIRY, JSON.stringify(refreshTokenExpireIn));
    }

    isStaffUserRefreshTokenEnabled() {
        return this.config.USE_STAFF_USER_REFRESH_TOKEN;
    }

    staffUserTokenData() {
        return this.staffManagementService.tokenDataDeviceResponse;
    }

    isNewStaffLogin() {
        return this.isStaffUserRefreshTokenEnabled() && this.localStorage.get(StorageKeys.STAFF_USER_REFRESH_TOKEN);
    }

    deleteStaffUserRefreshTokenFlowData() {
        if (this.isStaffUserRefreshTokenEnabled()) {
            this.localStorage.deleteValue(StorageKeys.token);
            this.localStorage.deleteValue(StorageKeys.moniepointAgentId);
            this.localStorage.deleteValue(StorageKeys.STAFF_USER_REFRESH_TOKEN);
            this.localStorage.deleteValue(StorageKeys.STAFF_USER_REFRESH_TOKEN_EXPIRY);
            this.staffManagementService.staffBusinesses = null;
            this.staffManagementService.tokenDataDeviceResponse = null;
        }
    }

    setStaffBusinesses(businesses) {
        this.staffManagementService.staffBusinesses = businesses;
    }

    saveTokenData(tokenData) {
        this.localStorage.save(StorageKeys.SAVED_TOKEN_DATA, JSON.stringify(tokenData));
    }

    async handleSessionNavigation(res) {
        this.canCreatePin(res) ? this.navigateToCreateUserPin(res?.validationKey, res?.tokenData?.domains) : this.navigateSignInUser();
    }

    async setSessionCredentials(res, registerFireBaseToken, deviceInformation) {
        this.isMobileWebviewUser = this.mobileService.isMobileWebViewUser();
        this.upDateDeviceInformation(deviceInformation);
        this.registerFireBaseToken = registerFireBaseToken;
        this.setSessionUserDetails(res);
        this.persistSignInData(res?.tokenData);
        this.loginZendesk(res?.zen_desk_access_token);
        await this.setFireBaseToken();
    }

    shouldCompleteSoftOnboarding(tokenData) {
        if (!this.config.CAN_USE_SOFT_ONBOARDING) {
            return false;
        }

        const roles = tokenData?.roles;
        const isRoleEligibleForSoftOnboarding = this.isRoleEligibleForSoftOnboarding(roles);

        const shouldIgnoreSoftOnboarding = !isRoleEligibleForSoftOnboarding || tokenData?.shouldCompleteKycBeforeSoftOnboarding;

        if (shouldIgnoreSoftOnboarding) {
            return false;
        }

        const domains = tokenData?.domains || this.getLoginDomains();

        if (this.hasBusinessAndPersonalBankingDomain(domains)) {
            return tokenData?.shouldCompleteSelfOnboardingKyc || this.shouldCompleteSelfOnboardingKyc();
        }

        return !this.hasBusinessBankingDomain(domains);
    }

    hasBusinessAndPersonalBankingDomain(domains: string[] = []) {
        const personalBankingDomain = domains?.includes(TOKEN_DOMAINS.MONIEPOINT_PERSONAL_BANKING);
        return this.hasBusinessBankingDomain(domains) && personalBankingDomain;
    }

    isRoleEligibleForSoftOnboarding(roles: string[] = []) {
        const eligibleRoles = RolesEligibleForSoftOnboarding;
        return roles.some((role) => eligibleRoles.includes(role));
    }

    getLoginDomains() {
        const domains = this.localStorage.get(StorageKeys.login_domains);
        if (!domains) {
            return [];
        }
        return [`${domains}`];
    }

    canCreatePin(res) {
        const shouldCreatePin = !res?.tokenData?.hasUserPin && res?.tokenData?.shouldCompleteSelfOnboardingKyc;
        return shouldCreatePin && this.config.USE_NEW_TRANSACTION_PIN;
    }

    async loginSessionUser(res, deviceInformation: DeviceInformation) {
        return new Promise(async (resolve) => {
            this.saveTokenData(res?.tokenData);
            this.deviceInformation = deviceInformation;
            await this.setEncryptedMobileDeviceId(deviceInformation);
            await this.setSessionCredentials(res, res?.notificationToken, deviceInformation);
            resolve(true);
        });
    }

    encryptMobileDeviceId(deviceId) {
        return this.encryptionUtil.encryptData(deviceId);
    }

    async saveEncryptedMobileDeviceId(encryptedDeviceId) {
        await this.deviceService.saveDeviceIdToIndexDb(encryptedDeviceId);
    }

    hasBusinessBankingDomain(domains: string[] = []) {
        return domains?.includes(TOKEN_DOMAINS.MONIEPOINT_BUSINESS_BANKING);
    }

    async setFireBaseToken() {
        if (!this.isMobileWebviewUser) {
            return;
        }
        await this.mobileWebViewHelperService.registerFirebaseToken(this.registerFireBaseToken);
    }

    upDateDeviceInformation(deviceInfo) {
        this.deviceInformation = deviceInfo || this.getDeviceInformation();
        if (!this.isMobileWebviewUser) {
            return;
        }
        const loginParameters = this.mobileService.getMoniepointAppParameters();
        const deviceInformation = loginParameters?.deviceRegistrationRequest;
        this.deviceInformation.model = deviceInformation?.model?.toUpperCase();
        this.deviceInformation.deviceUniqueIdentifier = deviceInformation?.deviceUniqueIdentifier;
        this.deviceInformation.manufacturer = deviceInformation?.manufacturer;
        this.setEncryptedMobileDeviceId(this.deviceInformation);
    }

    getDeviceInformation() {
        const deviceInfo = this.mobileService.getMoniepointAppParameters()?.deviceRegistrationRequest;
        const deviceInformation: DeviceInformation = {
            model: deviceInfo?.model,
            deviceUniqueIdentifier: deviceInfo?.deviceUniqueIdentifier,
            manufacturer: deviceInfo?.manufacturer,
        };
        return deviceInformation;
    }

    async setEncryptedMobileDeviceId(deviceInformation: DeviceInformation) {
        const encryptedDeviceId = this.encryptMobileDeviceId(deviceInformation?.deviceUniqueIdentifier);
        deviceInformation.encryptedDeviceId = encryptedDeviceId;
        await this.saveEncryptedMobileDeviceId(encryptedDeviceId);
    }

    setSessionUserDetails(res) {
        this.operationService.setUserSession(res);
        this.businesses = res.businesses || [];
        this.localStorage.handleBusinessesInResponse(res, this.businesses);
    }

    accessProtectedRoute() {
        this.navigateUserAccordingly();
    }

    emptyRoute() {
        this.router.navigateByUrl('/404').then(() => {
            this.closeModal();
        });
    }

    navigateUserBasedOnRole() {
        const role = StringHelper.getRoleWithRoute(this.getUserRole());
        this.router.navigateByUrl(`/main-app/${role.moduleRoute}`).then((res) => {
            this.closeModal();
        });
    }

    async getUserTypeInfo() {
        const offlineSalesSupportedPartners = [
            UserAlias.aggregator,
            UserAlias.superAggregator,
            UserAlias.supremeAggregator,
            UserAlias.subBRM,
            UserAlias.kioskMarketer,
        ];
        const token = JSON.parse(this.localStorage.get(StorageKeys.SAVED_TOKEN_DATA));
        if (offlineSalesSupportedPartners.some(element => token?.roles.includes(element))) {
            const userTypeResponse = await this.staffManagementService.getUserType();
            const {
                partnerCode = '',
                ...userTypeInfo
            } = userTypeResponse;
            this.localStorage.save(StorageKeys.SALES_PARTNER_CODE, partnerCode);
            this.localStorage.save(StorageKeys.userTypeInfo, JSON.stringify(userTypeInfo));
        }
    }

    getKMOnboardingStatus(tokenData) {
        if (tokenData?.roles?.includes(UserAlias.kioskMarketer)) {
            return this.staffManagementService.getKioskMarketerOnboardingStage();
        }
    }

    routeToBusinessSelection() {
        this.router.navigateByUrl('/user/selection-mode').then((res) => {
            this.closeModal();
        });
    }

    handleBrmNotVerifiedRoute(res) {
        if (!this.config.SHOW_COMPLIANCE_VIEW) {
            this.navigateUserBasedOnRole();
            return;
        }
        this.localStorage.save('reason', res?.reason);
        this.localStorage.save('approvalStatus', res?.approvalStatus);
        this.localStorage.save('bvn', res?.maskedBvn);
        this.localStorage.save('consolidation-expiry-date', res.consolidationExpiryDate);
        this.router.navigateByUrl('brm-compliance').then(() => {
            this.closeModal();
        });
    }

    handleBrmPendingRoute(res) {
        this.localStorage.save('consolidation-expiry-date', res.consolidationExpiryDate);
        this.router.navigateByUrl(`brm-compliance/consolidation-process?status=${res?.approvalStatus}`).then(() => {
            this.closeModal();
        });
    }

    handleBrmCompliance(res) {
        switch (res?.complianceStatus) {
            case 'NOT_VERIFIED':
            case undefined:
            case null:
            case '':
                this.handleBrmNotVerifiedRoute(res);
                break;
            case 'VERIFIED':
                this.navigateUserBasedOnRole();
                break;
            case 'PENDING':
                this.handleBrmPendingRoute(res);
        }
    }

    handleBrmComplianceError(err) {
        const description = StringHelper.getErrorMessageHttpErrorResponse(err);
        this.launchModal(description);
    }

    handleOncloseModal() {
        this.generalService.handleLogout();
        this.router.navigateByUrl('/login').then(() => this.modalService.closeAll());
    }

    launchModal(description) {
        this.modalService.closeAll();
        const config = new ModalOptionsConfig();
        config.nzComponentParams.alertType = 'error';
        config.nzComponentParams.title = 'Login failed';
        config.className = DEFAULT_MODAL_KAMONA_CLASSES;
        config.nzWidth = DEFAULT_MODAL_WIDTH;
        config.nzComponentParams.callback = () => {
            this.handleOncloseModal();
        };
        config.nzComponentParams.designType = MONIEPOINT_DESIGN_TYPE.KAMONA;
        config.nzComponentParams.callbackName = 'Go back to login';
        config.nzComponentParams.message = description;
        this.runningModal = this.modalService.alert(config);
    }

    handleBrmRouting() {
        const continueBrmRouting = this.config.SHOW_COMPLIANCE_VIEW || this.config.SHOW_CONSOLIDATION_VIEW;

        if (!continueBrmRouting) {
            this.navigateUserBasedOnRole();
            return;
        }

        this.complianceService
            .getComplianceStatus('brm')
            .then((res) => {
                this.handleBrmCompliance(res);
            })
            .catch((err) => {
                this.handleBrmComplianceError(err);
            });
    }

    getUserRole() {
        return this.userRole || this.localStorage.getUserRole();
    }

    private enforceFVSAddressOnboarding(params: { conRouteToLGAUpdateIfAllowed: boolean }) {
        this.router.navigateByUrl(`brm-compliance/${fvsConstants.routes.updateBRMLGAOnFVS}`, {
            state: {
                [fvsConstants.routeStateKeys.isFlagEnabled]: params.conRouteToLGAUpdateIfAllowed,
            },
        });
    }

    private async interceptToCheckFVSStatus() {
        try {
            const conRouteToLGAUpdateIfAllowed = this.fvsHelperService.routeToLGAUpdateIfAllowed();

            if (!conRouteToLGAUpdateIfAllowed) {
                return true;
            }

            const onboardedStatus = await this.fvsBRMLGAUpdateHelperService.getBRMOnboardingStatusOnFVS(conRouteToLGAUpdateIfAllowed);

            const canContinueOtherChecks = this.fvsBRMLGAUpdateHelperService.canContinueOtherLoginChecks(onboardedStatus);

            if (!canContinueOtherChecks) {
                this.enforceFVSAddressOnboarding({
                    conRouteToLGAUpdateIfAllowed,
                });
            }

            return canContinueOtherChecks;
        } catch (error) {
            return true;
        }
    }

    private async brmRouteChecks(toDashboard = false) {
        const isInterceptPassed = this.interceptToCheckFVSStatus();

        if (!isInterceptPassed) {
            return;
        }

        toDashboard ? this.routeToAggregatorDashboard() : this.handleBrmRouting();
    }

    navigateUserAccordingly(toDashboard = false) {
        const userRole = this.getUserRole();
        if (!userRole) {
            this.emptyRoute();
            return;
        }
        switch (userRole) {
            case UserAlias.agentManager:
            case UserAlias.staffUser:
            case UserAlias.staffAdmin:
                toDashboard ? this.routeToBusinessDashboard() : this.routeToBusinessSelection();
                break;
            case UserAlias.aggregator:
            case UserAlias.clusterManager:
                this.brmRouteChecks(toDashboard);
                break;
            case UserAlias.kioskMarketer:
                this.navigateKioskMarketer();
                break;
            default:
                this.navigateUserBasedOnRole();
        }
    }

    async navigateKioskMarketer() {
        const response = await this.kioskMarketerOnboardingStageRequest;
        const stage = response?.responseBody?.status;
        const route = stage !== 'COMPLETED' ? '/moniecrm/km-onboarding' : '/main-app/moniecrm/coming-soon';
        this.router.navigateByUrl(route).then((res) => {
            this.closeModal();
        });
    }

    navigateClusterManager() {
        this.router.navigateByUrl('/cm-role-selection').then((res) => {
            this.closeModal();
        });
    }

    routeToBusinessDashboard() {
        this.router.navigateByUrl('/main-app/business-user/dashboard').then((res) => {
            this.closeModal();
        });
    }

    routeToAggregatorDashboard() {
        this.router.navigateByUrl('/main-app/aggregator/brm-dashboard-v2/view').then((res) => {
            this.closeModal();
        });
    }

    completeUserKyc() {
        if (this.config.USE_NEW_KYC_FLOW) {
            this.router.navigateByUrl('/onboarding-v2/kyc').then(() => {
                this.closeModal();
                this.localStorage.setCompleteSelfOnboardingKyc(null);
            });
            return;
        }
        this.router.navigateByUrl(KYCFormRoute.PRIMER).then(() => {
            this.localStorage.setCompleteSelfOnboardingKyc(null);
            this.closeModal();
        });
    }

    registerBiometric(canIgnoreBiometric) {
        const hasPendingBiometricSetUp = this.mobileService.canRegisterBiometric();
        return hasPendingBiometricSetUp && this.isMobileWebviewUser && !canIgnoreBiometric;
    }

    routeToBiometricRegistration() {
        this.router.navigateByUrl('/biometric-registration').then((res) => {
            this.closeModal();
        });
    }

    handleAlreadyBiometricUser(staticAgent, tokenData) {
        const savedTokenData = tokenData ?? this.localStorage.getSaveTokenData();
        const shouldCompleteSoftOnboarding = this.shouldCompleteSoftOnboarding(savedTokenData);
        if (shouldCompleteSoftOnboarding) {
            this.routeToSoftOnboarding();
            return;
        }
        this.routeStaticUser(staticAgent);
    }

    routeStaticUser(staticAgent) {
        this.staticAgent = staticAgent || this.localStorage.shouldCompleteSelfOnboardingKyc();
        this.userRole = this.getUserRole();
        this.localStorage.save(StorageKeys.login_domains, '');
        this.staticAgent ? this.completeUserKyc() : this.accessProtectedRoute();
    }

    routeToSoftOnboarding() {
        this.onboardingService.setCurrentSoftOnBoardingStep(null);
        this.onboardingService.routeToSoftOnBoardingSplashScreen();
        this.closeModal();
    }

  navigateSignInUser(staticAgent = null, canIgnoreBiometric = false, tokenData = null) {
        const registerBiometric = this.registerBiometric(canIgnoreBiometric);
        registerBiometric ? this.routeToBiometricRegistration() : this.handleAlreadyBiometricUser(staticAgent, tokenData);
    }

    navigateToCreateUserPin(validationKey, domains: []) {
        this.localStorage.save(StorageKeys.validation_key_v2, validationKey);
        this.localStorage.save(StorageKeys.login_domains, domains.toString());
        this.router.navigateByUrl(KycRoutes.USER_PIN).then(() => {
            this.closeModal();
        });
    }

    closeModal() {
        this.modalService.closeAll();
    }

    setLoginOtpResponse(otpResponse: any) {
        this.loginOtpResponse = otpResponse;
    }

    setUserRole(role) {
        this.userRole = role;
    }

    setUserRoles(res) {
        const token = this.auth.getDecodedToken(res?.access_token);
        this.userRole = this.localStorage.setSelectedRole(token?.roles);
        this.localStorage.saveUserRole(this.userRole);
    }

    setKycData(shouldCompleteSelfOnboardingKyc) {
        this.staticAgent = this.config.ALLOW_KYC ? shouldCompleteSelfOnboardingKyc : false;
        if (this.config.ALLOW_KYC) {
            this.localStorage.save(StorageKeys.SHOULD_COMPLETE_SELF_ONBOARDING_KYC, JSON.stringify(this.staticAgent));
        }
    }

    shouldCompleteSelfOnboardingKyc() {
        const selfOnboardingKycStatus = this.localStorage.get(StorageKeys.SHOULD_COMPLETE_SELF_ONBOARDING_KYC);
        if (selfOnboardingKycStatus) {
            return JSON.parse(selfOnboardingKycStatus);
        }

        return false;
    }

    loginZendesk(token) {
        try {
            loginZendesk(token);
        } catch (error) {
            this.logZendeskEvent(error);
        }
    }

    logZendeskEvent(errorMessage) {
        const datalayer = initializeDatalayer('zendesk_sign_in_failed');
        datalayer.error_message = errorMessage;
        datalayer.api_error_message = errorMessage;
        this.dataLayerService.logEvent(datalayer);
    }

    initializeSignup() {
        try {
            this.onboardingService.setCurrentOnBoardingStep(null);
        } catch (error) {}
    }

    private persistSignInData(res) {
        const hasToken = res && res?.access_token;
        if (!hasToken) {
            return;
        }
        this.setKycData(res?.shouldCompleteSelfOnboardingKyc);
        this.setUserRoles(res);
        this.persistUserData(res);
    }

    private persistUserData(res) {
        const rawToken = res?.access_token;
        const token = this.auth.getDecodedToken(rawToken);
        const data = {
            token,
            encryptedDeviceId: this.deviceInformation.encryptedDeviceId,
            browser: this.deviceInformation.model,
            machineOS: this.deviceInformation.manufacturer,
            rawToken,
        };
        this.localStorage.save('email', res?.email);
        this.localStorage.save('zen_desk_access_token', res?.zen_desk_access_token);
        this.localStorage.persistUserData(data, this.staticAgent);
        this.localStorage.setSessionExpiration(token?.exp);
    }
}
